import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  BarChart3,
  Bot,
  Clock,
  FileText,
  LineChart as LineChartIcon,
  Repeat,
  Sparkles,
} from 'lucide-react';
import { api } from '../../api/client';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { SkeletonGrid } from '../../components/Skeleton';
import { BOT_COLORS } from '../../constants/platforms';
import { formatNumber, formatRelative } from '../../lib/format';

export default function BotActivityTab({ domain }) {
  const { data, isLoading } = useQuery({
    queryKey: ['crawlers', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/analytics/crawlers?days=30`)
        .then((r) => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const header = (
    <PageHeader
      variant="gradient"
      icon={Bot}
      eyebrow="Crawlers"
      title="AI bot activity"
      description="Track which AI training and search crawlers visit your site — GPTBot, ClaudeBot, PerplexityBot, Google-Extended and more. Confirms your content is reachable for AI engines."
      help={{
        title: 'Why this matters',
        body:
          'AI engines can only quote your site if their crawlers can read it. A healthy mix here means GEO is working; gaps suggest a robots.txt or bot-blocking issue.',
      }}
    />
  );

  if (isLoading)
    return (
      <div className="space-y-6">
        {header}
        <SkeletonGrid count={4} />
      </div>
    );
  if (!data) return null;

  const totalVisits = data.botStats.reduce((s, b) => s + b.visitCount, 0);
  const totalPages = data.botStats.reduce((s, b) => s + b.pagesCount, 0);

  if (totalVisits === 0) {
    return (
      <div className="space-y-6">
        {header}
        <EmptyState
          icon={Bot}
          title="No AI crawlers detected yet"
          body="Once GPTBot, ClaudeBot, PerplexityBot or similar crawlers visit your site, you'll see activity here. Make sure your tracking script is installed and that your robots.txt allows AI crawlers (Settings → Bot access)."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Bot visits · 30d"
          value={formatNumber(totalVisits)}
          icon={Bot}
          variant="brand"
        />
        <StatCard
          label="Pages crawled"
          value={formatNumber(totalPages)}
          icon={FileText}
          variant="cyan"
        />
        <StatCard
          label="Avg pages / visit"
          value={totalVisits ? (totalPages / totalVisits).toFixed(1) : '—'}
          icon={Repeat}
          variant="amber"
        />
        <StatCard
          label="Active bots"
          value={data.botStats.length}
          icon={Sparkles}
          variant="green"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <BarChart3 className="w-4 h-4" />
              </span>
              Visits by bot
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.botStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="botName" tick={{ fontSize: 11, fill: '#716b8b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#716b8b' }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Bar dataKey="visitCount" fill="#d61f97" name="Visits" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <LineChartIcon className="w-4 h-4" />
              </span>
              Crawl frequency over time
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#716b8b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#716b8b' }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Legend />
              {Object.keys(BOT_COLORS).map((bot) => (
                <Line
                  key={bot}
                  type="monotone"
                  dataKey={bot}
                  stroke={BOT_COLORS[bot]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Clock className="w-4 h-4" />
            </span>
            Recent crawler visits
          </div>
        </div>
        {data.recentVisits.length === 0 ? (
          <p className="text-sm text-gray-600">No visits logged yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {data.recentVisits.map((v) => (
              <li
                key={v._id}
                className="py-3 flex items-center justify-between text-sm gap-3 first:pt-0 last:pb-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="flex-none h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: BOT_COLORS[v.bot_id] || '#666' }}
                  >
                    <Bot className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-ink-900">{v.bot_name || v.bot_id}</div>
                    <div className="text-xs text-ink-400 truncate">{v.page}</div>
                  </div>
                </div>
                <div className="text-xs text-ink-400 whitespace-nowrap flex-none">
                  {formatRelative(v.timestamp)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
